<template>
  <div>
    <head>
      <meta charset="utf-8" />
      <title>Reset Password | Hospital Management System</title>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <meta content="Hospital Management System" name="description" />
      <meta content="Hospital Management Team" name="author" />
      <!-- App favicon -->
      <link rel="shortcut icon" href="assets/images/favicon.svg">

      <!-- Bootstrap Css -->
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <!-- Icons Css -->
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <!-- App Css-->
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body class="auth-body-bg">
    <div>
      <div class="container-fluid p-0">
        <div class="row g-0">

          <div class="col-xl-8 col-lg-8">
            <div class="auth-full-bg pt-lg-5 p-4">
              <div class="w-100">
                <div class="bg-overlay"></div>
                <div class="d-flex h-100 flex-column">
                  <div class="p-4 mt-auto">
                    <div class="row justify-content-center">
                      <div class="col-lg-7">
                        <div class="text-center">
                          <img src="../../public/logos/footer-logo.png" alt="Logo" class="img mb-3" style="width: 270px;">
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- end col -->

          <div class="col-xl-4 col-md-4">
            <div class="auth-full-page-content p-md-5 p-4">
              <div class="w-100">

                <div class="d-flex flex-column h-100">
                  <div class="mb-4 mb-md-5">
                    <a class="d-block auth-logo">
                      <img src="../../public/logos/moh-fdre-color.png" alt="">
                    </a>
                  </div>

                  <div class="my-auto">
                    <!-- Loading State -->
                    <div v-if="loading" class="text-center">
                      <div class="spinner-border text-primary m-2" role="status">
                        <span class="sr-only">Loading...</span>
                      </div>
                      <p class="text-muted mt-2">Validating reset link...</p>
                    </div>

                    <!-- Invalid Token State -->
                    <div v-else-if="!tokenValid" class="text-center">
                      <div class="avatar-md mx-auto mb-4">
                        <div class="avatar-title rounded-circle bg-light">
                          <i class="bx bx-x text-danger font-size-24"></i>
                        </div>
                      </div>
                      <h5 class="text-danger">Invalid or Expired Link</h5>
                      <p class="text-muted">{{ errorMessage }}</p>
                      <div class="mt-4">
                        <a href="/login" class="btn btn-primary w-100">
                          <i class="mdi mdi-arrow-left me-1"></i> Back to Login
                        </a>
                      </div>
                    </div>

                    <!-- Success State -->
                    <div v-else-if="passwordResetSuccess" class="text-center">
                      <div class="avatar-md mx-auto mb-4">
                        <div class="avatar-title rounded-circle bg-light">
                          <i class="bx bx-check text-success font-size-24"></i>
                        </div>
                      </div>
                      <h5 class="text-success">Password Reset Successful!</h5>
                      <p class="text-muted">Your password has been successfully reset. You can now log in with your new password.</p>
                      <div class="mt-4">
                        <a href="/login" class="btn btn-success w-100">
                          <i class="mdi mdi-login me-1"></i> Go to Login
                        </a>
                      </div>
                    </div>

                    <!-- Reset Password Form -->
                    <div v-else>
                      <div>
                        <h5 class="text-primary">Reset Password</h5>
                        <p class="text-muted">Enter your new password below</p>
                        <p v-if="userEmail" class="text-muted small">
                          <i class="mdi mdi-email-outline me-1"></i>
                          Resetting password for: <strong>{{ userEmail }}</strong>
                        </p>
                      </div>

                      <div class="mt-4">
                        <form @submit.prevent="resetPassword">
                          <!-- New Password -->
                          <div class="mb-3">
                            <label class="form-label">New Password</label>
                            <div class="input-group auth-pass-inputgroup">
                              <input
                                  v-model="passwordForm.newPassword"
                                  :type="showNewPassword ? 'text' : 'password'"
                                  class="form-control"
                                  :class="{'is-invalid': passwordErrors.newPassword}"
                                  placeholder="Enter new password"
                                  @input="validatePassword"
                                  required>
                              <button
                                  class="btn btn-light"
                                  type="button"
                                  @click="showNewPassword = !showNewPassword">
                                <i :class="showNewPassword ? 'mdi mdi-eye-off-outline' : 'mdi mdi-eye-outline'"></i>
                              </button>
                            </div>
                            <div v-if="passwordErrors.newPassword" class="invalid-feedback d-block">
                              {{ passwordErrors.newPassword }}
                            </div>

                            <!-- Password Strength Indicator -->
                            <div v-if="passwordForm.newPassword" class="mt-2">
                              <div class="progress" style="height: 5px;">
                                <div
                                    class="progress-bar"
                                    :class="passwordStrengthClass"
                                    :style="{width: passwordStrength + '%'}">
                                </div>
                              </div>
                              <p class="text-muted small mt-1">
                                Password strength: <span :class="passwordStrengthTextClass">{{ passwordStrengthText }}</span>
                              </p>
                            </div>

                            <!-- Password Requirements -->
                            <div class="mt-2">
                              <p class="text-muted small mb-2">Password must contain:</p>
                              <ul class="text-muted small mb-0" style="padding-left: 20px;">
                                <li :class="{'text-success': passwordRequirements.minLength}">
                                  <i :class="passwordRequirements.minLength ? 'mdi mdi-check' : 'mdi mdi-close'"></i>
                                  At least 8 characters
                                </li>
                                <li :class="{'text-success': passwordRequirements.hasUppercase}">
                                  <i :class="passwordRequirements.hasUppercase ? 'mdi mdi-check' : 'mdi mdi-close'"></i>
                                  One uppercase letter
                                </li>
                                <li :class="{'text-success': passwordRequirements.hasLowercase}">
                                  <i :class="passwordRequirements.hasLowercase ? 'mdi mdi-check' : 'mdi mdi-close'"></i>
                                  One lowercase letter
                                </li>
                                <li :class="{'text-success': passwordRequirements.hasNumber}">
                                  <i :class="passwordRequirements.hasNumber ? 'mdi mdi-check' : 'mdi mdi-close'"></i>
                                  One number
                                </li>
                                <li :class="{'text-success': passwordRequirements.hasSpecial}">
                                  <i :class="passwordRequirements.hasSpecial ? 'mdi mdi-check' : 'mdi mdi-close'"></i>
                                  One special character (!@#$%^&*)
                                </li>
                              </ul>
                            </div>
                          </div>

                          <!-- Confirm Password -->
                          <div class="mb-3">
                            <label class="form-label">Confirm Password</label>
                            <div class="input-group auth-pass-inputgroup">
                              <input
                                  v-model="passwordForm.confirmPassword"
                                  :type="showConfirmPassword ? 'text' : 'password'"
                                  class="form-control"
                                  :class="{'is-invalid': passwordErrors.confirmPassword}"
                                  placeholder="Confirm new password"
                                  @input="validatePasswordMatch"
                                  required>
                              <button
                                  class="btn btn-light"
                                  type="button"
                                  @click="showConfirmPassword = !showConfirmPassword">
                                <i :class="showConfirmPassword ? 'mdi mdi-eye-off-outline' : 'mdi mdi-eye-outline'"></i>
                              </button>
                            </div>
                            <div v-if="passwordErrors.confirmPassword" class="invalid-feedback d-block">
                              {{ passwordErrors.confirmPassword }}
                            </div>
                            <div v-if="passwordsMatch && passwordForm.confirmPassword" class="valid-feedback d-block">
                              <i class="mdi mdi-check"></i> Passwords match
                            </div>
                          </div>

                          <!-- Alert Messages -->
                          <div v-if="alert.success" class="alert alert-success" role="alert">
                            <i class="mdi mdi-check-circle-outline me-2"></i>
                            {{ message }}
                          </div>

                          <div v-if="alert.failed" class="alert alert-danger" role="alert">
                            <i class="mdi mdi-alert-outline me-2"></i>
                            {{ message }}
                          </div>

                          <!-- Submit Button -->
                          <div class="mt-4 d-grid">
                            <button
                                type="submit"
                                class="btn btn-primary waves-effect waves-light"
                                :disabled="submitting || !isFormValid">
                              <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              {{ submitting ? 'Resetting Password...' : 'Reset Password' }}
                            </button>
                          </div>

                          <!-- Back to Login Link -->
                          <div class="mt-4 text-center">
                            <a href="javascript: void(0);" class="text-muted" @click="loginPage">
                              <i class="mdi mdi-arrow-left me-1"></i> Back to Login
                            </a>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>

                  <div class="mt-4 mt-md-5 text-center">
                    <p class="mb-0">© CBHI</p>
                  </div>
                </div>

              </div>
            </div>
          </div>
          <!-- end col -->
        </div>
        <!-- end row -->
      </div>
      <!-- end container-fluid -->
    </div>
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";
import router from "@/router";

export default {
  name: "ResetPasswordPage",
  data() {
    return {
      loading: true,
      submitting: false,
      tokenValid: false,
      passwordResetSuccess: false,

      // Token from URL
      resetToken: '',
      userEmail: '',

      // Password form
      passwordForm: {
        newPassword: '',
        confirmPassword: ''
      },

      // Password visibility
      showNewPassword: false,
      showConfirmPassword: false,

      // Password validation
      passwordErrors: {
        newPassword: '',
        confirmPassword: ''
      },

      passwordRequirements: {
        minLength: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasSpecial: false
      },

      passwordStrength: 0,
      passwordStrengthText: '',
      passwordStrengthClass: '',
      passwordStrengthTextClass: '',
      passwordsMatch: false,

      // Alert states
      alert: {
        success: false,
        failed: false
      },
      message: '',
      errorMessage: 'This password reset link is invalid or has expired. Please request a new one.'
    }
  },

  computed: {
    isFormValid() {
      return this.passwordStrength >= 60 &&
          this.passwordsMatch &&
          this.passwordForm.newPassword &&
          this.passwordForm.confirmPassword &&
          !this.passwordErrors.newPassword &&
          !this.passwordErrors.confirmPassword
    }
  },

  mounted() {
    // Get token from URL - handle both hash and regular query params
    let token = null;

    // First try to get from hash-based URL (Vue Router)
    if (window.location.hash) {
      const hashParts = window.location.hash.split('?');
      if (hashParts.length > 1) {
        const urlParams = new URLSearchParams(hashParts[1]);
        token = urlParams.get('token');
      }
    }

    // Fallback to regular query params
    if (!token) {
      const urlParams = new URLSearchParams(window.location.search);
      token = urlParams.get('token');
    }

    this.resetToken = token;

    console.log('Token extracted:', this.resetToken); // Debug log

    if (!this.resetToken) {
      this.tokenValid = false
      this.loading = false
      this.errorMessage = 'No reset token provided. Please use the link from your email.'
      return
    }

    // Validate the token
    this.validateToken()
  },

  methods: {
    async validateToken() {
      console.log('Validating token:', this.resetToken); // Debug log

      try {
        const data = {
          function: "validateResetToken",
          token: this.resetToken
        }

        console.log('Sending request:', data); // Debug log

        const response = await executeJson(data, authUrl)

        console.log('Response received:', response.data); // Debug log

        if (response.data.success && response.data.data && response.data.data.valid) {
          this.tokenValid = true
          this.userEmail = response.data.data.email
          console.log('Token is valid for email:', this.userEmail);
        } else {
          this.tokenValid = false
          this.errorMessage = response.data.message || response.data.data?.message || this.errorMessage
          console.log('Token validation failed:', this.errorMessage);
        }
      } catch (error) {
        console.error('Error validating token:', error);
        this.tokenValid = false
        this.errorMessage = 'Failed to validate reset link. Please try again.'
      } finally {
        this.loading = false
      }
    },

    loginPage(){
      router.push('/')
    },

    validatePassword() {
      const password = this.passwordForm.newPassword

      // Reset errors
      this.passwordErrors.newPassword = ''

      // Check requirements
      this.passwordRequirements.minLength = password.length >= 8
      this.passwordRequirements.hasUppercase = /[A-Z]/.test(password)
      this.passwordRequirements.hasLowercase = /[a-z]/.test(password)
      this.passwordRequirements.hasNumber = /[0-9]/.test(password)
      this.passwordRequirements.hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)

      // Calculate strength
      let strength = 0
      if (this.passwordRequirements.minLength) strength += 20
      if (this.passwordRequirements.hasUppercase) strength += 20
      if (this.passwordRequirements.hasLowercase) strength += 20
      if (this.passwordRequirements.hasNumber) strength += 20
      if (this.passwordRequirements.hasSpecial) strength += 20

      // Extra points for length
      if (password.length >= 12) strength = Math.min(100, strength + 10)
      if (password.length >= 16) strength = 100

      this.passwordStrength = strength

      // Set strength text and classes
      if (strength < 40) {
        this.passwordStrengthText = 'Weak'
        this.passwordStrengthClass = 'bg-danger'
        this.passwordStrengthTextClass = 'text-danger'
      } else if (strength < 60) {
        this.passwordStrengthText = 'Fair'
        this.passwordStrengthClass = 'bg-warning'
        this.passwordStrengthTextClass = 'text-warning'
      } else if (strength < 80) {
        this.passwordStrengthText = 'Good'
        this.passwordStrengthClass = 'bg-info'
        this.passwordStrengthTextClass = 'text-info'
      } else {
        this.passwordStrengthText = 'Strong'
        this.passwordStrengthClass = 'bg-success'
        this.passwordStrengthTextClass = 'text-success'
      }

      // Validate minimum requirements
      if (password && !this.passwordRequirements.minLength) {
        this.passwordErrors.newPassword = 'Password must be at least 8 characters long'
      } else if (password && strength < 60) {
        this.passwordErrors.newPassword = 'Password is too weak. Please follow the requirements below.'
      }

      // Check if passwords match
      if (this.passwordForm.confirmPassword) {
        this.validatePasswordMatch()
      }
    },

    validatePasswordMatch() {
      this.passwordErrors.confirmPassword = ''

      if (this.passwordForm.confirmPassword &&
          this.passwordForm.newPassword !== this.passwordForm.confirmPassword) {
        this.passwordErrors.confirmPassword = 'Passwords do not match'
        this.passwordsMatch = false
      } else if (this.passwordForm.confirmPassword) {
        this.passwordsMatch = true
      }
    },

    async resetPassword() {
      // Clear alerts
      this.alert.success = false
      this.alert.failed = false

      // Final validation
      if (!this.isFormValid) {
        this.alert.failed = true
        this.message = 'Please ensure all password requirements are met and passwords match.'
        return
      }

      this.submitting = true

      try {
        const data = {
          function: "resetPasswordWithToken",
          token: this.resetToken,
          newPassword: this.passwordForm.newPassword,
          confirmPassword: this.passwordForm.confirmPassword
        }

        const response = await executeJson(data, authUrl)

        if (response.data.success) {
          this.alert.success = true
          this.message = response.data.message || 'Password has been reset successfully!'
          this.passwordResetSuccess = true

          // Redirect to login after 3 seconds
          setTimeout(() => {
            router.push('/')
          }, 3000)
        } else {
          this.alert.failed = true
          this.message = response.data.message || 'Failed to reset password. Please try again.'
        }
      } catch (error) {
        this.alert.failed = true
        this.message = error.message || 'An error occurred. Please try again.'
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.auth-pass-inputgroup .btn {
  border-left: 0;
}

.auth-pass-inputgroup .form-control {
  border-right: 0;
}

.auth-pass-inputgroup .form-control:focus {
  box-shadow: none;
  border-color: #ced4da;
}

.auth-pass-inputgroup .form-control:focus + .btn {
  border-color: #80bdff;
}

.progress {
  background-color: #f3f3f3;
}

.avatar-md {
  height: 5rem;
  width: 5rem;
}

.avatar-title {
  align-items: center;
  background-color: #556ee6;
  color: #fff;
  display: flex;
  font-weight: 500;
  height: 100%;
  justify-content: center;
  width: 100%;
}

.is-invalid {
  border-color: #f46a6a !important;
}

.invalid-feedback {
  color: #f46a6a;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.valid-feedback {
  color: #34c38f;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

ul li {
  list-style: none;
  margin-bottom: 0.25rem;
}

ul li i {
  margin-right: 0.25rem;
}
</style>